// ============================================================================
// Client for the NEW unified reporting engine (Distributed-Systems API).
//
// AUTH — the browser NEVER talks to the engine and NEVER holds engine
// credentials (they used to be Vite-inlined into the shipped bundle; that is
// exactly what this design removes). All requests go to a same-origin proxy
// that authenticates the caller and attaches the engine credential
// server-side:
//
//   dev   → "/new-reporting-api"  Vite dev proxy + newReportingAuthPlugin
//           (vite.config.js) log in to the local engine server-side.
//   prod  → "/api/reporting"      Vercel serverless function
//           (api/reporting-proxy.ts). It validates the caller's
//           admin-app session (Hasura JWT from localStorage "ron-auth",
//           sent as the Bearer token below) and requires the Ronin-staff
//           role (role_id === 1) — the server-side twin of the
//           requireRoninAdmin route guard — before forwarding with the
//           server-held service account.
//
// The bases are hardcoded on purpose: no VITE_ env var can point this client
// somewhere else or leak anything into the bundle.
// ============================================================================

import {
  CommissaryListResponse,
  EventOption,
  LocationOption,
  OrderItemsListResponse,
  ReconciliationParams,
  ReconciliationResponse,
  ReportQueryParams,
  ReportResponse,
  TabManagementListResponse,
  TransactionsListResponse,
  VendorOption,
  WebCheckoutListResponse,
} from "./types";

const BASE = import.meta.env.DEV ? "/new-reporting-api" : "/api/reporting";

// True for the rejection fetch throws when its AbortSignal fires. Hooks use this to stay
// silent on cancellation (a superseded/aborted request must not surface as an error).
export function isAbortError(e: unknown): boolean {
  return e instanceof DOMException ? e.name === "AbortError" : (e as { name?: string })?.name === "AbortError";
}

export class NewReportingApiError extends Error {
  status: number;
  detail?: string;
  constructor(message: string, status: number, detail?: string) {
    super(message);
    this.name = "NewReportingApiError";
    this.status = status;
    this.detail = detail;
  }
}

// GET {BASE}{path}, authenticated with the admin-app session token. The proxy
// (dev plugin / serverless function) swaps it for an engine token server-side.
// `signal` (optional) lets a caller abort the request — passed straight to fetch,
// so an aborted request rejects with a DOMException named "AbortError" that we
// re-throw untouched (callers detect it to stay silent on cancellation).
async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const session = localStorage.getItem("ron-auth");

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      headers: {
        Accept: "application/json",
        ...(session ? { Authorization: `Bearer ${session}` } : {}),
      },
      signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e; // caller cancelled — pass through
    throw new NewReportingApiError(
      `Could not reach the reporting proxy at ${BASE}${path}` +
        (import.meta.env.DEV
          ? " — is Ronin.ApiService running and the Vite dev proxy up?"
          : " — the /api/reporting serverless function did not respond."),
      0,
      e instanceof Error ? e.message : String(e)
    );
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new NewReportingApiError(
      res.status === 401
        ? import.meta.env.DEV
          ? "401 from the new reporting engine — the dev auth plugin could not log in (is the engine up? check VITE_NEW_REPORTING_EMAIL / _PASSWORD in .env)."
          : "Your admin-app session has expired or is invalid — log in again and reopen this page."
        : res.status === 403
        ? "Your account is not authorized for the new reporting pages (Ronin staff only)."
        : `New reporting API request failed (${res.status}) for ${path}`,
      res.status,
      body
    );
  }
  return (await res.json()) as T;
}

function buildQueryString(params: ReportQueryParams): string {
  const sp = new URLSearchParams();
  sp.set("reportType", params.reportType);
  if (params.dimension) sp.set("dimension", params.dimension);
  if (params.from) sp.set("from", params.from);
  if (params.to) sp.set("to", params.to);
  if (params.eventId) sp.set("eventId", params.eventId);
  if (params.vendorId) sp.set("vendorId", params.vendorId);
  if (params.locationId) sp.set("locationId", params.locationId);
  if (params.limit != null) sp.set("limit", String(params.limit));
  return sp.toString();
}

// GET /reports/query — the unified reporting engine. Pass `signal` to make it abortable.
export async function queryReport(
  params: ReportQueryParams,
  signal?: AbortSignal
): Promise<ReportResponse> {
  return apiGet<ReportResponse>(`/reports/query?${buildQueryString(params)}`, signal);
}

// GET /reports/detail — row-level reconciliation export. Same params as /reports/query.
// reportType must be a detail-list type (TransactionsList / OrderItemsList).
export async function queryTransactionsDetail(
  params: ReportQueryParams,
  signal?: AbortSignal
): Promise<TransactionsListResponse> {
  return apiGet<TransactionsListResponse>(`/reports/detail?${buildQueryString(params)}`, signal);
}

export async function queryOrderItemsDetail(
  params: ReportQueryParams,
  signal?: AbortSignal
): Promise<OrderItemsListResponse> {
  return apiGet<OrderItemsListResponse>(`/reports/detail?${buildQueryString(params)}`, signal);
}

// GET /reports/detail with reportType=TabManagementList — one row per POS tab order (RP-2166).
export async function queryTabManagementDetail(
  params: ReportQueryParams,
  signal?: AbortSignal
): Promise<TabManagementListResponse> {
  return apiGet<TabManagementListResponse>(`/reports/detail?${buildQueryString(params)}`, signal);
}

// GET /reports/detail with reportType=WebCheckoutList — one row per legacy web-checkout order (RP-2182).
export async function queryWebCheckoutDetail(
  params: ReportQueryParams,
  signal?: AbortSignal
): Promise<WebCheckoutListResponse> {
  return apiGet<WebCheckoutListResponse>(`/reports/detail?${buildQueryString(params)}`, signal);
}

// GET /reports/detail with reportType=CommissaryList — one row per commissary order (RP-2173),
// vendor-grouped by the page from the server-computed vendorTotals.
export async function queryCommissaryDetail(
  params: ReportQueryParams,
  signal?: AbortSignal
): Promise<CommissaryListResponse> {
  return apiGet<CommissaryListResponse>(`/reports/detail?${buildQueryString(params)}`, signal);
}

// GET /events/options — slim {id,name} list for the event filter dropdown.
export async function getEventOptions(): Promise<EventOption[]> {
  return apiGet<EventOption[]>(`/events/options`);
}

// GET /vendors/options and /locations/options — {id,name} lists for the filter dropdowns.
export async function getVendorOptions(): Promise<VendorOption[]> {
  return apiGet<VendorOption[]>(`/vendors/options`);
}

export async function getLocationOptions(): Promise<LocationOption[]> {
  return apiGet<LocationOption[]>(`/locations/options`);
}

// GET /reports/reconciliation — composite OLD-vs-NEW reconciliation for an event scope.
// No reportType/limit; eventId required.
export async function queryReconciliation(
  params: ReconciliationParams
): Promise<ReconciliationResponse> {
  const sp = new URLSearchParams();
  sp.set("eventId", params.eventId);
  if (params.from) sp.set("from", params.from);
  if (params.to) sp.set("to", params.to);
  if (params.vendorId) sp.set("vendorId", params.vendorId);
  if (params.locationId) sp.set("locationId", params.locationId);
  return apiGet<ReconciliationResponse>(`/reports/reconciliation?${sp.toString()}`);
}
